import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsService from '~/services/SettingsService';
import type { Setting, WorkingHour } from '~/types/api';

/**
 * Site ayarlarını yönetmek için composable
 */
export function useSettings() {
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  
  const siteSettings = reactive<Record<string, any>>({
    site_title: '',
    site_description: '',
    logo: '',
    dark_logo: '',
    favicon: ''
  });
  
  const contactInfo = reactive<Record<string, any>>({
    address: '',
    phone: '',
    email: '',
    working_hours: [],
    maps: ''
  });
  
  const socialMedia = reactive<Record<string, string>>({
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
    whatsapp: ''
  });
  
  const policies = reactive<Record<string, string>>({
    cookie_policy: '',
    kvkk_policy: ''
  });
  
  const settingsService = SettingsService.getInstance();
  const { locale } = useI18n();
  
  /**
   * Tüm site ayarlarını yükle
   */
  const fetchAllSettings = async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
      // Site genel ayarlarını al
      const siteData = await settingsService.getSiteSettings(locale.value);
      Object.assign(siteSettings, siteData);
      
      // İletişim bilgilerini al
      const contactData = await settingsService.getContactInfo(locale.value);
      Object.assign(contactInfo, contactData);
      
      // Sosyal medya bilgilerini al
      const socialData = await settingsService.getSocialMediaSettings(locale.value);
      Object.assign(socialMedia, socialData);
      
      // Politika bilgilerini al
      const allSettings = await settingsService.getAllSettings(locale.value);
      const policyKeys = ['cookie_policy', 'kvkk_policy'];
      
      policyKeys.forEach(key => {
        const setting = allSettings.find(s => s.key === key);
        if (setting && typeof setting.value === 'string') {
          policies[key] = setting.value;
        }
      });
      
    } catch (err) {
      console.error('Site ayarları yüklenirken hata oluştu:', err);
      error.value = 'Site ayarları yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.';
    } finally {
      isLoading.value = false;
    }
  };
  
  /**
   * Belirli bir ayarı getir
   * @param key Ayar anahtarı
   */
  const getSettingByKey = async (key: string): Promise<any> => {
    try {
      const setting = await settingsService.getSettingByKey(key, locale.value);
      return setting ? setting.value : null;
    } catch (err) {
      console.error(`'${key}' ayarı alınırken hata oluştu:`, err);
      return null;
    }
  };
  
  // Component oluşturulduğunda verileri otomatik yükle
  onMounted(() => {
    fetchAllSettings();
  });
  
  // Dil değişimini izle ve yeniden veri çek
  watch(() => locale.value, () => {
    fetchAllSettings();
  });
  
  return {
    isLoading,
    error,
    siteSettings,
    contactInfo,
    socialMedia,
    policies,
    fetchAllSettings,
    getSettingByKey
  };
} 